import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    Alert,
    Box,
    Button,
    Chip,
    FormControl,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import FolderSpecialOutlinedIcon from "@mui/icons-material/FolderSpecialOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import { getApiBaseUrl } from "../../../config/api";
import { getSchoolIdForUser } from "../../../utils/userContext";
import {
    EmptyResultsRow,
    KpiCard,
    ResultsPageContainer,
    ResultsPageHeader,
    ResultsSectionPaper,
    ResponsiveTableWrap,
} from "./resultsUi";
import {
    TermRecordsScopeAlert,
    TermRecordsScopeTabs,
} from "../../../components/term/TermRecordsScopeTabs";
import { useTermRecordsScope } from "../../../hooks/useTermRecordsScope";
import { buildTermRecordsQuery } from "../../../utils/termRecordsScope";

const LEVEL_FILTERS = ["All", "Primary", "Secondary", "University"];

export default function ResultsRecords() {
    const navigate = useNavigate();
    const { currentUser } = useSelector((s) => s.user);
    const schoolId = getSchoolIdForUser(currentUser);
    const { recordsScope, setRecordsScope, currentTermLabel } = useTermRecordsScope(schoolId);

    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [levelFilter, setLevelFilter] = useState("All");

    useEffect(() => {
        if (!schoolId) return;
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const { data } = await axios.get(
                    `${getApiBaseUrl()}/SchoolResultsOverview/${schoolId}`,
                    {
                        params: buildTermRecordsQuery({
                            recordsScope,
                            extra: { publishedOnly: "true" },
                        }),
                    },
                );
                if (!cancelled) {
                    setRows(Array.isArray(data) ? data : []);
                    setError("");
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err.response?.data?.message || "Could not load results records");
                    setRows([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [schoolId, recordsScope]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return rows.filter((row) => {
            if (levelFilter !== "All" && row.educationLevel !== levelFilter) return false;
            if (!q) return true;
            return String(row.sclassName || "").toLowerCase().includes(q);
        });
    }, [rows, search, levelFilter]);

    const summary = useMemo(() => {
        const totalClasses = filtered.length;
        const totalPublished = filtered.reduce(
            (s, r) => s + (Number(r.publishedRecordsCount) || 0),
            0,
        );
        return { totalClasses, totalPublished };
    }, [filtered]);

    return (
        <ResultsPageContainer>
            <ResultsPageHeader
                eyebrow="Official archive"
                title="Results records"
                subtitle="When you approve end-of-term packages, marks move here automatically and the class workspace is cleared for the next term."
                action={
                    <Button
                        variant="outlined"
                        startIcon={<FactCheckOutlinedIcon />}
                        onClick={() => navigate("/Admin/exams/review")}
                    >
                        Approve new packages
                    </Button>
                }
            />

            <TermRecordsScopeTabs value={recordsScope} onChange={setRecordsScope} />
            <TermRecordsScopeAlert
                scope={recordsScope}
                currentTermLabel={currentTermLabel}
                sectionLabel="published results"
            />

            {error ? (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                    {error}
                </Alert>
            ) : null}

            {rows.length === 0 && !loading ? (
                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                    No published results yet. When a form teacher sends class results, approve them under{" "}
                    <strong>Approve class results</strong> — they will appear here automatically.
                </Alert>
            ) : null}

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                    <KpiCard
                        title="Classes with records"
                        value={summary.totalClasses}
                        icon={FolderSpecialOutlinedIcon}
                        color="primary"
                        loading={loading}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <KpiCard
                        title="Published periods"
                        value={summary.totalPublished}
                        icon={FolderSpecialOutlinedIcon}
                        color="success"
                        loading={loading}
                    />
                </Grid>
            </Grid>

            <ResultsSectionPaper title="Select a class">
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
                    <TextField
                        size="small"
                        placeholder="Search class…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ minWidth: { xs: "100%", sm: 220 } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlinedIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Level</InputLabel>
                        <Select label="Level" value={levelFilter} onChange={(e) => setLevelFilter(e.target.value)}>
                            {LEVEL_FILTERS.map((l) => (
                                <MenuItem key={l} value={l}>
                                    {l}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>

                <ResponsiveTableWrap>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Class</TableCell>
                                <TableCell>Latest period</TableCell>
                                <TableCell align="right">Published sets</TableCell>
                                <TableCell align="right">Avg % (latest)</TableCell>
                                <TableCell align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <EmptyResultsRow colSpan={5} message="Loading…" />
                            ) : filtered.length === 0 ? (
                                <EmptyResultsRow colSpan={5} message="No published records for this filter." />
                            ) : (
                                filtered.map((row) => (
                                    <TableRow key={row.classId} hover>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                                                <Box component="span" sx={{ fontWeight: 600 }}>
                                                    {row.sclassName}
                                                </Box>
                                                <Chip label={row.educationLevel} size="small" variant="outlined" />
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {[row.latestTerm, row.latestReportPeriod, row.latestAcademicYear]
                                                    .filter(Boolean)
                                                    .join(" · ") || "—"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">{row.publishedRecordsCount}</TableCell>
                                        <TableCell align="right">
                                            {Number(row.averageMark || 0).toFixed(1)}%
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Open published results for this class">
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    onClick={() =>
                                                        navigate(`/Admin/exams/records/${row.classId}`)
                                                    }
                                                >
                                                    View results
                                                </Button>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </ResponsiveTableWrap>
            </ResultsSectionPaper>
        </ResultsPageContainer>
    );
}
